import { useState } from 'react';
import { FileText, Upload } from 'lucide-react';

interface SeletorDeArquivoProps {
  /** Vai no <input> escondido, para o <label htmlFor> do formulário continuar apontando para ele. */
  id: string;
  accept: string;
  arquivo: File | null;
  onChange: (arquivo: File | null) => void;
  /** Ex.: ".pfx ou .p12". Aparece embaixo do "Clique para escolher". */
  dica?: string;
  disabled?: boolean;
}

function tamanhoLegivel(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

/**
 * Campo de arquivo em português. O <input type="file"> nativo escreve "Choose file / No file chosen"
 * no idioma do navegador e não aceita outro texto, então ele fica invisível (mas focável pelo
 * teclado) dentro de uma área clicável que também aceita arrastar e soltar.
 */
export function SeletorDeArquivo({ id, accept, arquivo, onChange, dica, disabled = false }: SeletorDeArquivoProps) {
  const [arrastando, setArrastando] = useState(false);
  const [focado, setFocado] = useState(false);

  const destacado = arrastando || focado;

  return (
    <label
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setArrastando(true);
      }}
      onDragLeave={() => setArrastando(false)}
      onDrop={(e) => {
        e.preventDefault();
        setArrastando(false);
        if (!disabled) onChange(e.dataTransfer.files?.[0] ?? null);
      }}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        width: '100%',
        boxSizing: 'border-box',
        padding: 'var(--space-4)',
        border: `2px dashed ${destacado ? 'var(--color-primary-500)' : 'var(--color-border-default)'}`,
        borderRadius: 'var(--radius-lg)',
        background: 'var(--color-bg-root)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'border-color 0.2s ease',
      }}
    >
      <input
        id={id}
        type="file"
        accept={accept}
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.files?.[0] ?? null);
          // Limpa o campo nativo: assim escolher o mesmo arquivo de novo também dispara o onChange.
          e.target.value = '';
        }}
        onFocus={() => setFocado(true)}
        onBlur={() => setFocado(false)}
        style={{ position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden', pointerEvents: 'none' }}
      />
      {arquivo ? (
        <>
          <FileText size={24} color="var(--color-primary-500)" style={{ flexShrink: 0 }} />
          <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)', fontWeight: 'var(--font-medium)', overflowWrap: 'anywhere' }}>
              {arquivo.name}
            </span>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
              {tamanhoLegivel(arquivo.size)} · clique para trocar
            </span>
          </span>
        </>
      ) : (
        <>
          <Upload size={24} color="var(--color-text-secondary)" style={{ flexShrink: 0 }} />
          <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)', fontWeight: 'var(--font-medium)' }}>
              Clique para escolher o arquivo
            </span>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
              {dica ? `${dica} · ou arraste até aqui` : 'ou arraste até aqui'}
            </span>
          </span>
        </>
      )}
    </label>
  );
}
